/*
 * Navalli cart drawer extras (2026-10-13)
 * - "Why not add..." suggestions from Shopify product recommendations, added through the theme's own
 *   add-to-cart flow ([data-add-to-cart] inside a form), so the drawer refreshes as usual.
 * - Shows the checkout total in the same format as the line prices (the theme writes it with the currency code).
 */
(function () {
  const MAX_RECS = 6;
  // "Why not add..." switched off for now (Alex, 2026-10-13). Set back to true to show it again.
  const SHOW_RECS = false;
  let lastProductId = null;
  let busy = false;

  const drawer = () => document.querySelector('[data-cart-drawer]');
  const recsEl = () => drawer()?.querySelector('[data-navalli-recs]');

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency}).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  }

  function syncCheckoutTotal() {
    const root = drawer();
    const total = root?.querySelector('[data-cart-price-holder] [data-cart-total]');
    const button = root?.querySelector('[data-cart-total-price]');
    if (total && button) button.textContent = total.textContent.trim();
  }

  function imageUrl(src, width) {
    if (!src) return '';
    const url = src.startsWith('//') ? 'https:' + src : src;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  }

  // Swatch colors come from the theme's own map (assets/swatches.json), so they match the product page
  let swatchMapPromise = null;

  function swatchKey(value) {
    return String(value).toLowerCase().replace(/\s+/g, '');
  }

  function loadSwatchMap() {
    if (swatchMapPromise) return swatchMapPromise;
    const url = recsEl()?.dataset.swatchUrl;
    swatchMapPromise = !url
      ? Promise.resolve({})
      : fetch(url)
          .then((r) => r.json())
          .then((data) => {
            const map = {};
            (data.colors || []).forEach((entry) => Object.entries(entry).forEach(([name, value]) => (map[swatchKey(name)] = value)));
            return map;
          })
          .catch(() => ({}));
    return swatchMapPromise;
  }

  function swatchStyle(value, map) {
    const found = map[swatchKey(value)];
    if (!found) return 'background:#E5E5E5';
    if (found.startsWith('#') || found.startsWith('rgb')) return `background:${found}`;
    const base = recsEl()?.dataset.swatchUrl || '';
    return `background:url('${base.replace(/swatches\.json.*$/, encodeURIComponent(found))}') center / cover`;
  }

  function swatchOptionIndex(product) {
    const names = (recsEl()?.dataset.swatchNames || 'Color, Colour').split(',').map((n) => n.trim().toLowerCase());
    return (product.options || []).findIndex((o) => names.includes(String(o.name || o).toLowerCase()) && (o.values || []).length > 1);
  }

  const bagIcon = `<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><g transform="translate(-1 -0.25)"><path d="M15.5 19.5H5.5l1-11h11l.4 4.5"/><path d="M9 8.5V7a3 3 0 0 1 6 0v1.5"/><path d="M18 15.5v5M15.5 18h5"/></g></svg>`;

  function card(product, map) {
    const variants = product.variants.filter((v) => v.available);
    const first = variants[0];
    const compare = first.compare_at_price && first.compare_at_price > first.price ? first.compare_at_price : null;
    const url = product.url || window.theme.routes.root + 'products/' + product.handle;
    const swatchIndex = swatchOptionIndex(product);
    // Any other option with a real choice (a size, a length) has to be picked on the product page
    const needsProductPage = (product.options || []).some((o, i) => i !== swatchIndex && (o.values || []).length > 1);

    let swatches = '';
    if (swatchIndex > -1) {
      const key = 'option' + (swatchIndex + 1);
      const values = [...new Set(variants.map((v) => v[key]))];
      swatches = `<div class="navalli-rec__swatches" role="radiogroup" aria-label="${escapeHtml(product.options[swatchIndex].name)}">
          ${values
            .map(
              (value, i) =>
                `<button type="button" class="navalli-swatch${value === first[key] ? ' is-selected' : ''}" style="${swatchStyle(value, map)}"
                  role="radio" aria-checked="${value === first[key]}" aria-label="${escapeHtml(value)}" title="${escapeHtml(value)}"
                  data-navalli-swatch data-option="${key}" data-value="${escapeHtml(value)}"></button>`
            )
            .join('')}
        </div>`;
    }

    const action = needsProductPage
      ? `<a class="navalli-rec__add" href="${url}" aria-label="Choose options for ${escapeHtml(product.title)}">${bagIcon}</a>`
      : `<button type="submit" class="navalli-rec__add" data-add-to-cart aria-label="Add ${escapeHtml(product.title)} to bag">${bagIcon}</button>`;

    return `
      <li class="navalli-rec" data-product-form data-variants='${escapeHtml(JSON.stringify(variants.map((v) => ({id: v.id, price: v.price, compare: v.compare_at_price, image: v.featured_image?.src || null, o1: v.option1, o2: v.option2, o3: v.option3}))))}'>
        <a class="navalli-rec__image" href="${url}" tabindex="-1">
          <img src="${imageUrl(first.featured_image?.src || product.featured_image, 300)}" alt="${escapeHtml(product.title)}" width="150" height="150" loading="lazy" data-navalli-rec-image>
        </a>
        <form class="navalli-rec__form" action="${window.theme.routes.cart_add_url}" method="post">
          <a class="navalli-rec__title" href="${url}">${escapeHtml(product.title)}</a>
          <p class="navalli-rec__price" data-navalli-rec-price>
            <span>${formatMoney(first.price)}</span>${compare ? ` <del>${formatMoney(compare)}</del>` : ''}
          </p>
          <input type="hidden" name="id" value="${first.id}">
          <input type="hidden" name="quantity" value="1">
          <div class="navalli-rec__row">
            ${swatches}
            ${action}
          </div>
          <div data-cart-errors-container></div>
        </form>
      </li>`;
  }

  // Swatch click: pick that color's variant and show its photo and price
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-navalli-swatch]');
    if (!swatch) return;
    event.preventDefault();

    const cardEl = swatch.closest('.navalli-rec');
    const variants = JSON.parse(cardEl.dataset.variants || '[]');
    const short = swatch.dataset.option.replace('option', 'o');
    const variant = variants.find((v) => v[short] === swatch.dataset.value);
    if (!variant) return;

    cardEl.querySelector('input[name="id"]').value = variant.id;
    cardEl.querySelectorAll('[data-navalli-swatch]').forEach((el) => {
      el.classList.toggle('is-selected', el === swatch);
      el.setAttribute('aria-checked', String(el === swatch));
    });
    if (variant.image) cardEl.querySelector('[data-navalli-rec-image]').src = imageUrl(variant.image, 300);
    const compare = variant.compare && variant.compare > variant.price ? variant.compare : null;
    cardEl.querySelector('[data-navalli-rec-price]').innerHTML = `<span>${formatMoney(variant.price)}</span>${compare ? ` <del>${formatMoney(compare)}</del>` : ''}`;
  });

  async function renderRecs() {
    const el = recsEl();
    if (!el || busy) return;
    if (!SHOW_RECS) {
      el.hidden = true;
      return;
    }
    busy = true;

    try {
      const cart = await fetch(window.theme.routes.root + 'cart.js').then((r) => r.json());
      if (!cart.items.length) {
        el.hidden = true;
        lastProductId = null;
        return;
      }

      // Newest line first in cart.js: base suggestions on the latest piece added
      const productId = cart.items[0].product_id;
      const inCart = new Set(cart.items.map((item) => item.product_id));

      if (productId !== lastProductId || !el.innerHTML) {
        const url = `${window.theme.routes.product_recommendations_url}.json?product_id=${productId}&limit=10&intent=related`;
        const data = await fetch(url).then((r) => r.json());
        el.dataset.products = JSON.stringify(data.products || []);
        lastProductId = productId;
      }

      const products = JSON.parse(el.dataset.products || '[]')
        .filter((p) => p.available && !inCart.has(p.id) && p.variants.some((v) => v.available))
        .slice(0, MAX_RECS);

      if (!products.length) {
        el.hidden = true;
        return;
      }

      const map = await loadSwatchMap();
      el.innerHTML = `
        <p class="navalli-recs__heading">Why not add&hellip;</p>
        <ul class="navalli-recs__list">${products.map((p) => card(p, map)).join('')}</ul>`;
      el.hidden = false;
    } catch (error) {
      console.log(error);
      el.hidden = true;
    } finally {
      busy = false;
    }
  }

  // Stock note at the top of the bag: visible while the bag has items (replaced the countdown timer, 2026-10-14)
  try { localStorage.removeItem('navalli-cart-timer'); } catch (e) {}

  async function updateNotice() {
    const note = drawer()?.querySelector('[data-navalli-notice]');
    if (!note) return;
    const cart = await fetch(window.theme.routes.root + 'cart.js').then((r) => r.json()).catch(() => null);
    if (cart) note.hidden = !cart.item_count;
  }

  function refresh() {
    updateNotice();
    syncCheckoutTotal();
    renderRecs();
  }

  // Personalized products (YMQ Product Options) need their name fields, which the quick-add popup and the
  // one-tap add can't show. YMQ assigns its option sets by product tag or product ID; read those rules and send
  // the quick-buy bag on those products to the product page instead (Alex, 2026-10-14).
  function ymqRules() {
    const assign = window.ymq_option && window.ymq_option.assign;
    if (!assign || typeof assign !== 'object') return null;
    const tags = new Set();
    const contains = [];
    const ids = new Set();
    Object.values(assign).forEach((set) => {
      const manual = (set && set.manual) || {};
      String(manual.tag || '').split(',').map((t) => t.trim().toLowerCase()).filter(Boolean).forEach((t) => tags.add(t));
      (manual.product || []).forEach((id) => ids.add(String(id)));
      Object.values((set && set.products_automate_data) || {}).forEach((group) =>
        Object.values(group || {}).forEach((rule) => {
          const value = String((rule && rule.condition_value) || '').trim().toLowerCase();
          if (value) contains.push(value);
        })
      );
    });
    return {tags, contains, ids};
  }

  function isPersonalized(card) {
    const rules = ymqRules();
    if (!rules) return false;
    if (rules.ids.has(String(card.dataset.navalliId || ''))) return true;
    const cardTags = String(card.dataset.navalliTags || '').split('|').map((t) => t.trim()).filter(Boolean);
    return cardTags.some((t) => rules.tags.has(t) || rules.contains.some((c) => t.includes(c)));
  }

  document.addEventListener(
    'click',
    (event) => {
      const quick = event.target.closest('.product-grid-item .btn--quick');
      if (!quick) return;
      const card = quick.closest('.product-grid-item');
      if (!card || !card.dataset.navalliUrl || !isPersonalized(card)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.href = card.dataset.navalliUrl;
    },
    true
  );

  document.addEventListener('theme:cart:loaded', refresh);
  document.addEventListener('theme:cart:added', refresh);
  document.addEventListener('theme:cart:open', () => {
    updateNotice();
    renderRecs();
  });
})();
